import { useDispatch, useSelector } from 'react-redux'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { State } from 'src/state'
import { getCurrentUser, patchCurrentUser } from 'src/state/users'
import { categoriesSelector } from 'src/state/selectors'
import { MenuSection, UserPatchType } from '@meniudigital/shared'
import Setting from '../Setting'
import MdToggle from '../../MdToggle'
import { SECTION_OPTIONS } from '../../CategoryModal'

export default function MultipleSectionsSetting() {
  const dispatch = useDispatch<ThunkDispatch<State, undefined, AnyAction>>()
  const { user } = useSelector((state: State) => state.users)
  const categoryState = useSelector(categoriesSelector)

  if (!user) {
    return null
  }

  const hasCategories = (section: MenuSection, onlyVisible: boolean) =>
    categoryState.list.some(
      category => (category.type || MenuSection.Restaurant) === section && (!onlyVisible || !category.isHidden),
    )

  // The web menu's tabs: sections that have categories, plus Restaurant always (getAvailableSections on the server)
  const sectionOptions = SECTION_OPTIONS.filter(
    option => option.value === MenuSection.Restaurant || hasCategories(option.value, false),
  )
  const savedSection = user.defaultMenuSection || MenuSection.Restaurant
  // A saved section with no visible categories left makes the server open Restaurant, so the pill shows that
  const defaultSection = hasCategories(savedSection, true) ? savedSection : MenuSection.Restaurant

  const confirmToggle = async () => {
    if (!user.isUsingMenuSections) {
      await window.showModal({
        title: 'Activare meniuri multiple',
        text: 'Ești sigur că dorești activarea meniurilor multiple?\n\nVei putea organiza produsele pe secțiuni diferite (Restaurant, Bar, Vinuri, etc.).',
        confirmButtonText: 'Da, activează',
        cancelButtonText: 'Nu',
      })
    } else {
      await window.showModal({
        title: 'Dezactivare meniuri multiple',
        text: 'Ești sigur că dorești dezactivarea meniurilor multiple?\n\nToate produsele vor fi afișate într-un singur meniu.',
        confirmButtonText: 'Da, dezactivează',
        cancelButtonText: 'Nu',
      })
    }
  }

  const toggleMultipleSections = () => {
    dispatch(patchCurrentUser({ type: UserPatchType.IsUsingMenuSections, data: !user.isUsingMenuSections }))
  }

  const confirmDefaultSection = async (newValue: MenuSection) => {
    if (newValue === defaultSection) {
      return
    }
    const label = sectionOptions.find(option => option.value === newValue)?.label

    try {
      await window.showModal({
        title: 'Schimbare secțiune implicită',
        text: `Ești sigur că dorești ca scanarea codului QR să deschidă „${label}”?\n\nClienții vor putea trece oricând la celelalte secțiuni.`,
        confirmButtonText: 'Da, schimbă',
        cancelButtonText: 'Nu',
      })
    } catch {
      return
    }

    const result = await dispatch(patchCurrentUser({ type: UserPatchType.DefaultMenuSection, data: newValue }))

    // The pill moved before the save (optimistic update in the users slice): put it back when the save fails
    if (!patchCurrentUser.fulfilled.match(result)) {
      dispatch(getCurrentUser())
    }
  }

  return (
    <Setting
      title="Meniuri / Secțiuni Multiple"
      iconPath="/icons/books-stacked.png"
      isEnabled={user.isUsingMenuSections}
      onBeforeToggle={confirmToggle}
      onToggle={toggleMultipleSections}
      toggleTexts={{ on: 'Active', off: 'Inactive' }}
      body={
        sectionOptions.length >= 2 ? (
          <>
            <div className="setting-label">Secțiunea deschisă implicit la scanarea codului QR</div>
            <MdToggle
              value={defaultSection}
              options={sectionOptions}
              onChange={confirmDefaultSection}
              flipsOnActiveClick={false}
            />
          </>
        ) : undefined
      }
      infoText="Permite meniuri separate pe secțiuni (Restaurant, Bar, Vinuri, etc.). Dezactivează pentru un meniu unic."
    />
  )
}
